import * as React from 'react'
import { Popup } from '@/ui/overlays/Popup'
import { PopupStepUploading } from '@/modules/imports/ui/PopupStepUploading'
import { StepCommonPending } from './StepCommonPending'
import { getImportStepMessages, isPendingStep } from './config'
import { useImportFlow } from './useImportFlow'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { PopupStepImportingInner } from '@/modules/imports/ui/PopupStepImportingInner'
import { PopupStepImportSummary } from '@/modules/imports/ui/step-import-summary/PopupStepImportSummary'
import { getProgressConfig } from './utils'

type Props = {
  isOpen?: boolean
  handleClose?: () => void
  companyId: string
  onSuccess?: () => void
}

export const ImportPopupContainer = ({
  isOpen,
  handleClose,
  companyId,
  onSuccess,
}: Props) => {
  const { t } = useLocalize()
  const IMPORT_STEP_MESSAGES = getImportStepMessages(t)

  const {
    step,
    handleFinish,
    handleDone,
    handleImport,
    handleUploadFiles,
    handleRestart,
    handleAbortAndReset,
    uploadDetails,
    importResult,
    isImportResultPending,
    importTask,
    filesCount,
    uploadedFilesCount,
    logsCount,
    summarySnapshot,
  } = useImportFlow({
    handleClose,
    companyId,
    onSuccess,
  })

  const handleCloseOrCancel = () => {
    handleAbortAndReset()
    handleClose()
  }

  // From "Zaimportuj" until the save task finishes there is nothing useful the
  // user can do, and leaving mid-save is what produced the "I opened Activities
  // and my data was not there" reports: the write is still running in the
  // fileprocessor. So the dialog holds them - no X, no backdrop, no Escape and
  // no abort button - until the import is actually saved.
  const isSaving = step === 'sending:importData' || step === 'polling:importStatus'

  // Real progress from the import task, once the backend starts publishing it.
  // Falls back to the timed animation until then (and for the upload phase, which
  // the client measures itself), so this can only ever improve the bar.
  const backendProgress = React.useMemo(() => {
    const total = importTask?.progressTotal ?? 0
    const current = importTask?.progressCurrent ?? 0
    if (!total || total <= 0) return null
    return Math.min(Math.max(current / total, 0), 1)
  }, [importTask?.progressCurrent, importTask?.progressTotal])

  return (
    <>
      <Popup
        open={isOpen}
        onClose={isSaving ? undefined : handleCloseOrCancel}
        closeOnBackdrop={!isSaving}
        closeOnEscape={!isSaving}
        showCloseButton={!isSaving}
        contentClassName="tw-min-w-[80vw]"
      >
        {/* <PopupStepImportSummary
         uploadDetails={mock as any as ImportsReadModel}
          task={mockTask}
          handleSubmit={handleFinish}
        />  */}
        {isPendingStep(step) && (
          <StepCommonPending
            title={IMPORT_STEP_MESSAGES[step]?.title || ''}
            // While the save runs the backend reports the stage it is actually in
            // (saving drivers / vehicles / recalculating violations). Prefer that
            // over the static blurb - it is the difference between "please wait"
            // and knowing what the import is doing.
            description={
              (isSaving && importTask?.message) ||
              IMPORT_STEP_MESSAGES[step]?.description ||
              ''
            }
            onAbort={isSaving ? undefined : handleCloseOrCancel}
            {...(isSaving && backendProgress !== null
              ? { progress: backendProgress, durationMs: 400 }
              : getProgressConfig({
                  step,
                  filesCount,
                  uploadedFilesCount,
                  logsCount,
                }))}
          />
        )}
        {step === 'user:uploadFiles' && (
          <PopupStepUploading
            handleUpload={handleUploadFiles}
            isUploading={false}
          />
        )}
        {step === 'user:mapAndSelect' && (
          <PopupStepImportingInner
            handleImport={handleImport}
            readModel={uploadDetails}
            companyId={companyId}
            handleCancel={handleRestart}
            handleDone={handleDone}
          />
        )}
        {step === 'user:confirmSummary' && (
          <PopupStepImportSummary
            uploadDetails={uploadDetails}
            importResult={importResult}
            isResultPending={isImportResultPending}
            task={importTask}
            handleSubmit={handleFinish}
            summarySnapshot={summarySnapshot}
          />
        )}
      </Popup>
    </>
  )
}
